import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { StatusPill } from "@/components/admin/AdminUI";
import { apiResendInvite, apiRevokeInvite, formatApiError } from "@/lib/api";
import { formatShortDate } from "@/lib/date";
import { Mail, RotateCw, XCircle, AlertTriangle } from "lucide-react";

const INVITE_TTL_DAYS = 7;
const RED_FLAG_AFTER_DAYS = 5;

function daysBetween(fromISO, toISO) {
    if (!fromISO || !toISO) return null;
    const a = new Date(fromISO);
    const b = new Date(toISO);
    const diff = Math.floor((b - a) / (1000 * 60 * 60 * 24));
    return diff;
}

function ageInDays(user) {
    // Age = invite creation → now. Prefer created_at; fall back to expires - TTL.
    const nowISO = new Date().toISOString();
    if (user.created_at) return Math.max(0, daysBetween(user.created_at, nowISO) ?? 0);
    if (user.invite_expires_at) {
        const created = new Date(new Date(user.invite_expires_at).getTime() - INVITE_TTL_DAYS * 86400000).toISOString();
        return Math.max(0, daysBetween(created, nowISO) ?? 0);
    }
    return 0;
}

function daysRemaining(user) {
    if (!user.invite_expires_at) return null;
    const nowISO = new Date().toISOString();
    return daysBetween(nowISO, user.invite_expires_at);
}

export default function ActiveInvitesPanel({ users, onChanged }) {
    const pending = useMemo(
        () =>
            (users || [])
                .filter((u) => u.invite_status === "Invited")
                .map((u) => ({ ...u, _age: ageInDays(u), _remaining: daysRemaining(u) }))
                .sort((a, b) => b._age - a._age),
        [users],
    );

    const [confirmRevoke, setConfirmRevoke] = useState(null);
    const [busyId, setBusyId] = useState(null);

    async function handleResend(u) {
        setBusyId(u.user_id);
        try {
            const res = await apiResendInvite(u.user_id);
            if (res?.email_status?.sent) {
                toast.success(`Invite resent to ${u.email}`);
            } else if (res?.email_status?.attempted) {
                toast.error(
                    `Invite refreshed but email did not send: ${res.email_status.error || "unknown error"}`,
                );
            } else {
                toast.success(`Invite refreshed for ${u.email}`);
            }
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
        }
    }

    async function handleRevoke() {
        if (!confirmRevoke) return;
        setBusyId(confirmRevoke.user_id);
        try {
            await apiRevokeInvite(confirmRevoke.user_id);
            toast.success("Invite revoked");
            setConfirmRevoke(null);
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
        }
    }

    if (pending.length === 0) return null;

    const staleCount = pending.filter((u) => u._age >= RED_FLAG_AFTER_DAYS).length;

    return (
        <div
            data-testid="active-invites-panel"
            className="bg-white border border-ntt-border rounded-xl overflow-hidden mb-6"
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-ntt-border bg-[#FBFCFE]">
                <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#0072BC]" />
                    <h3 className="text-[14px] font-semibold text-[#070F26]">
                        Active Invites
                    </h3>
                    <span
                        data-testid="active-invites-count"
                        className="inline-flex items-center h-5 px-2 rounded-full bg-[#DBEAF6] text-[#0072BC] text-[11px] font-medium"
                    >
                        {pending.length}
                    </span>
                    {staleCount > 0 && (
                        <span
                            data-testid="active-invites-stale-count"
                            className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-red-50 text-red-700 text-[11px] font-medium"
                        >
                            <AlertTriangle className="h-3 w-3" />
                            {staleCount} stale
                        </span>
                    )}
                </div>
                <span className="text-[11px] text-ntt-muted">
                    Red flag after {RED_FLAG_AFTER_DAYS} of {INVITE_TTL_DAYS} days
                </span>
            </div>

            <div className="grid grid-cols-[minmax(200px,2fr)_minmax(180px,1.6fr)_120px_140px_200px] gap-4 px-5 py-2 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                <div>Name</div>
                <div>Email</div>
                <div>Role</div>
                <div>Age</div>
                <div className="text-right">Actions</div>
            </div>

            {pending.map((u) => {
                const stale = u._age >= RED_FLAG_AFTER_DAYS;
                const expired = (u._remaining ?? 1) <= 0;
                return (
                    <div
                        key={u.user_id}
                        data-testid={`pending-invite-${u.user_id}`}
                        className={`grid grid-cols-[minmax(200px,2fr)_minmax(180px,1.6fr)_120px_140px_200px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center ${
                            stale ? "bg-red-50/40" : ""
                        }`}
                    >
                        <div className="min-w-0">
                            <div className="text-[13px] font-medium text-[#070F26] truncate">
                                {u.full_name}
                            </div>
                            <div className="text-[11px] text-ntt-muted truncate">
                                Invited {u.created_at ? formatShortDate(u.created_at) : "—"}
                            </div>
                        </div>
                        <div className="text-[13px] text-[#2B2F3A] truncate">{u.email}</div>
                        <div>
                            <StatusPill label={u.role} tone={u.role === "Team Lead" ? "amber" : "blue"} />
                        </div>
                        <div className="flex items-center gap-2">
                            <span
                                data-testid={`pending-invite-age-${u.user_id}`}
                                className={`text-[12px] font-mono ${stale ? "text-red-700 font-semibold" : "text-[#2B2F3A]"}`}
                            >
                                {u._age} / {INVITE_TTL_DAYS} d
                            </span>
                            {expired && (
                                <span className="inline-flex items-center h-5 px-1.5 rounded bg-red-100 text-red-800 text-[10px] font-semibold">
                                    Expired
                                </span>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResend(u)}
                                disabled={busyId === u.user_id}
                                data-testid={`resend-invite-${u.user_id}`}
                                className="h-8 text-[12px]"
                                title="Refresh the token, extend the expiry, and email the invite again"
                            >
                                <RotateCw
                                    className={`h-3.5 w-3.5 mr-1 ${busyId === u.user_id ? "animate-spin" : ""}`}
                                />
                                Resend
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmRevoke(u)}
                                disabled={busyId === u.user_id}
                                data-testid={`revoke-invite-${u.user_id}`}
                                className="h-8 text-[12px] text-red-600 hover:text-red-700 hover:border-red-300"
                            >
                                <XCircle className="h-3.5 w-3.5 mr-1" />
                                Revoke
                            </Button>
                        </div>
                    </div>
                );
            })}

            <AlertDialog
                open={!!confirmRevoke}
                onOpenChange={(o) => !o && setConfirmRevoke(null)}
            >
                <AlertDialogContent data-testid="confirm-revoke-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke this invite?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmRevoke?.full_name || "This user"} will be removed from
                            the pending list and the invite token will stop working. You can
                            always invite them again later.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-revoke-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-revoke-ok"
                            onClick={handleRevoke}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Revoke invite
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
